<template>
    <div class="detail-popup-container">
        <popup-modal @setAction="handleSetAction" @cancelAction="handleCancelAction">
            <div class="popup-wrapper">
                <p class="title">Details</p>
                <div class="subtitle">
                    <p>Plug in these details to spotlight your venue.</p>
                    <p>They'll be front and center on your profile.</p>
                </div>

                <div class="field-inputs">
                    <div class="venue-phone-input">
                        <label for="venue-phone">Phone number</label>
                        <input
                            v-model="formValues.phone"
                            :class="formErrors.phone.error ? 'invalid-input' : ''"
                            @input="validate('phone')"
                            @change="validate('phone')"
                            @blur="validate('phone')"
                            @keydown="handleFormatPhoneNumber($event)"
                            type="text"
                            maxlength="16"
                            name="venue-phone"
                            placeholder="Enter a phone number of your venue"
                        />
                        <div v-if="formErrors.phone.error" class="input-err-msg">
                            {{ formErrors.phone.msg }}
                        </div>
                    </div>
                    <div class="venue-email-input">
                        <label for="venue-email">E-mail address</label>
                        <input
                            v-model="formValues.email"
                            :class="formErrors.email.error ? 'invalid-input' : ''"
                            @input="validate('email')"
                            type="email"
                            maxlength="50"
                            name="venue-email"
                            placeholder="Enter an e-mail address of your venue"
                        />
                        <div v-if="formErrors.email.error" class="input-err-msg">
                            {{ formErrors.email.msg }}
                        </div>
                    </div>
                    <div class="venue-website-input">
                        <label for="venue-website">Website</label>
                        <input
                            v-model="formValues.website"
                            :class="formErrors.website.error ? 'invalid-input' : ''"
                            @input="validate('website')"
                            type="text"
                            maxlength="50"
                            name="venue-website"
                            placeholder="Enter a website of your venue"
                        />
                        <div v-if="formErrors.website.error" class="input-err-msg">
                            {{ formErrors.website.msg }}
                        </div>
                    </div>
                    <div class="venue-slug-input">
                        <label for="venue-slug">Hedlyner</label>
                        <input
                            v-model="formValues.slug"
                            :class="formErrors.slug.error ? 'invalid-input' : ''"
                            @input="validate('slug')"
                            type="text"
                            maxlength="50"
                            name="venue-slug"
                            placeholder="Enter your URL handle for your venue"
                        />
                        <div v-if="formErrors.slug.error" class="input-err-msg">
                            {{ formErrors.slug.msg }}
                        </div>
                    </div>
                </div>
                <div v-if="compErrMsg" class="input-err-msg">
                    {{ compErrMsg }}
                </div>
            </div>
        </popup-modal>
    </div>
</template>
<script>
    import axios from 'axios'
    import PopupModal from '@/components/user/login-popups/PopupModal.vue'
    import useFormattedData from '@/composables/useFormattedData.js'

    const URLS = {
        user: '/profile/v1/settings/user/',
        info: '/profile/v1/info/venue/'
    }

    const inputValid = {
        phone: (val) =>
            !!(
                val === '' ||
                /^[0-9]{10}$/.test(val) ||
                /^\([0-9]{3}\)[0-9]{3}-[0-9]{4}$/.test(val)
            ),
        email: (val) =>
            !!(
                val === '' ||
                /^[a-zA-Z0-9.-_]+@[a-zA-Z-_]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?$/.test(val)
            ),
        website: (val) =>
            !!(
                val === '' ||
                /^(http:\/\/|https:\/\/)?(www.)?[0-9a-zA-Z-_.]+\.[a-zA-Z]{2,3}[a-zA-Z0-9_/-]*$/.test(
                    val
                )
            ),
        slug: (val) => !!(val === '' || /^[a-zA-Z0-9-_]+$/.test(val))
    }

    export default {
        name: 'Detail',

        components: {
            'popup-modal': PopupModal
        },

        emits: ['closeModalAction'],

        data: () => ({
            formValues: {
                phone: '',
                email: '',
                website: '',
                slug: ''
            },

            formErrors: {
                phone: {
                    error: false,
                    msg: 'Please enter 10 digit number with format: (123)456-7890'
                },
                email: {
                    error: false,
                    msg: 'Please enter email with format: test@email.com'
                },
                website: {
                    error: false,
                    msg: 'Please enter website with format: www.xxxx.xxx/xxx'
                },
                slug: {
                    error: false,
                    msg: 'Please enter an unique slug identifier using alphanumeric characters.'
                }
            },

            compErrMsg: ''
        }),

        setup() {
            const { formatPhoneNumberInput } = useFormattedData()
            return {
                format: {
                    formatPhoneNumberInput
                }
            }
        },

        methods: {
            clearErrors() {
                for (const [key, val] of Object.entries(this.formErrors)) {
                    this.$set(this.formErrors, key, { ...val, error: false })
                }

                this.compErrMsg = ''
            },

            prepInfoData() {
                let data = {}

                if (this.formValues.phone) {
                    data.venue_phone = this.formValues.phone
                }

                if (this.formValues.email) {
                    data.venue_email = this.formValues.email
                }

                if (this.formValues.website) {
                    data.web_site = this.formValues.website
                }

                if (this.formValues.slug) {
                    data.slug = this.formValues.slug
                }

                return data
            },

            async handleSetAction() {
                this.clearErrors()

                if (this.validate()) {
                    let infoData = this.prepInfoData()

                    if (Object.keys(infoData).length) {
                        await this.updateInfo(infoData)
                        this.$emit('closeModalAction')
                    } else {
                        this.compErrMsg = '[422] : You must enter some values to continue.'
                    }
                }
            },

            handleCancelAction() {
                for (const key of Object.keys(this.formValues)) {
                    this.$set(this.formValues, key, '')
                }

                this.clearErrors()
                this.$emit('closeModalAction')
            },

            handleFormatPhoneNumber(event) {
                this.formValues.phone = this.format.formatPhoneNumberInput(
                    event,
                    this.formValues.phone
                )
            },

            validate(name) {
                let keys = Object.keys(this.formErrors)

                if (name) return this.validateInput(name)

                let isInputValid = true

                for (let i = 0; i < keys.length; i++) {
                    if (!inputValid[keys[i]](this.formValues[keys[i]])) {
                        this.formErrors[keys[i]].error = true
                        isInputValid = false
                    } else {
                        this.formErrors[keys[i]].error = false
                    }
                }

                return isInputValid
            },

            validateInput(name) {
                if (!inputValid[name](this.formValues[name])) {
                    this.formErrors[name].error = true
                    return false
                } else {
                    this.formErrors[name].error = false
                    return true
                }
            },

            async updateInfo(data) {
                try {
                    let userResp = await axios.get(this.URLS.user)
                    let userId = userResp.data.data[0].id

                    let infoResp = await axios.post(this.URLS.info + userId, {
                        _method: 'PUT',
                        ...data
                    })
                    return infoResp
                } catch (err) {
                    this.compErrMsg = `[${err.response?.status}] : ${err.response?.data?.message}`
                    throw err
                }
            }
        },

        computed: {
            URLS: () => URLS
        }
    }
</script>
<style lang="scss" scoped>
    .popup-wrapper {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        margin-left: 90px;

        .title {
            margin-top: 50px;
            color: #dedede;
            font-size: 44px;
            font-family: Instrument Sans;
            font-style: normal;
            font-weight: 500;
            line-height: 110%;
            letter-spacing: -1.76px;
        }

        .subtitle {
            margin-top: 22px;

            p {
                color: #acacac;
                font-size: 16px;
                font-family: Instrument Sans;
                font-style: normal;
                font-weight: 550;
                line-height: 125%;
                margin-top: 0px;
                margin-bottom: 0px;
            }
        }

        .field-inputs {
            margin-top: 28px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 25px;
        }

        label {
            display: block;
            color: #8b8b8b;
            font-size: 20px;
            font-family: Instrument Sans;
            font-style: normal;
            font-weight: 550;
            line-height: 120%;
            letter-spacing: -0.2px;
            margin-bottom: 10px;
        }

        input {
            min-width: 980px;
            padding: 12px 15px 15px 15px;
            border-radius: 12px;
            border: 1.5px solid #8b8b8b;
            background: #262626;
            color: #8b8b8b;
            font-size: 20px;
            font-family: Instrument Sans;
            font-style: normal;
            font-weight: 550;
            line-height: 120%;
            letter-spacing: -0.2px;
        }

        input::placeholder {
            color: #8b8b8b;
            font-size: 20px;
            font-family: Instrument Sans;
            font-style: normal;
            font-weight: 550;
            line-height: 120%;
            letter-spacing: -0.2px;
        }
    }
</style>
